import { ActionIcon, Button, Checkbox, Menu, Select, TextInput } from '@mantine/core';
import { IconDots, IconKey, IconSearch, IconUserPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { sortItems } from '../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { setupErrorText } from '../settings/RolesScreen.js';
import classes from '../settings/Setup.module.css';
import { setupErrorCode, setupErrorDetail } from '../settings/setup-source.js';
import {
  ChangeEmailDialog,
  PasswordConfirm,
  type TemporaryLogin,
} from '../settings/UsersScreen.js';
import { CreateLoginDialog } from './CrewCardDialog.js';
import own from './LoginAccountsScreen.module.css';
import {
  filterLoginAccounts,
  type LoginAccountAction,
  type LoginAccountFilter,
  type LoginAccountItem,
  type LoginAccountList,
  type LoginAccountsSource,
  loginAccountActions,
  loginAccountNote,
  loginAccountNotice,
  onBoardText,
  passwordText,
} from './login-accounts.js';

const PAGE_SIZE = 15;

type Column = 'name' | 'type' | 'onBoard' | 'status';

/**
 * Satırın sıralama anahtarı "On board sign-in" için: önce gemiye inmemiş değişiklikler (bekleyen
 * parola, gemide hâlâ geçerli kaldırma), sonra hazır, sonra diğerleri; en sonda "Office only".
 */
const ONBOARD_ORDER: Record<string, number> = {
  waiting: 0,
  disabledPending: 0,
  noPasswordPending: 0,
  signedOffPending: 0,
  removed: 0,
  ready: 1,
  noPassword: 3,
  disabled: 4,
  noServer: 5,
  joins: 6,
  signedOff: 7,
  noLogin: 8,
};
const OFFICE_ONLY_ORDER = 9;

/** Rütbe sütununa girmeyen satırlar: görevi bitmiş ya da ataması silinmiş. */
const ENDED_STATES = new Set(['signedOff', 'signedOffPending', 'removed']);

export interface LoginAccountsTableProps {
  list: Pick<
    LoginAccountList,
    'vessels' | 'canCreate' | 'canEdit' | 'canManage' | 'actorId' | 'actorIsAdmin'
  >;
  accounts: LoginAccountItem[];
  onAction: (account: LoginAccountItem, action: LoginAccountAction) => void;
  /** Ekranın "şimdi"si (test); verilmezse çizim anı. */
  now?: Date;
}

/**
 * Giriş hesapları tablosu (ADR 0147 §2): Name + Login e-mail, Type, Rank / role, On board sign-in
 * (gemi başına satır), Password, Status, işlemler. Birincil işlem satırda düğmedir ("New password"
 * ya da "Create login"); diğerleri satır menüsünde.
 */
export function LoginAccountsTable({ list, accounts, onAction, now }: LoginAccountsTableProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`loginAccounts.${key}`, options);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(1);
  const vesselById = new Map(list.vessels.map((v) => [v.id, v]));
  const value: Record<Column, (a: LoginAccountItem) => string | number> = {
    name: (a) => a.fullName,
    type: (a) => a.type,
    onBoard: (a) =>
      Math.min(
        ...a.vessels.map((v) => ONBOARD_ORDER[v.state] ?? OFFICE_ONLY_ORDER),
        OFFICE_ONLY_ORDER,
      ),
    status: (a) => (a.hasLogin ? (a.active ? 0 : 1) : 2),
  };
  const sorted = sortItems(accounts, sort.dir, value[sort.key]);
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const page_ = Math.min(page, pages);
  const visible = sorted.slice((page_ - 1) * PAGE_SIZE, page_ * PAGE_SIZE);
  const rank = (a: LoginAccountItem) => {
    const ranks = [
      ...new Set(a.vessels.filter((v) => !ENDED_STATES.has(v.state)).map((v) => v.rank)),
    ];
    const items = [...ranks, ...a.officeRoles];
    return items.length > 0 ? items.join(', ') : (a.vessels[0]?.rank ?? '—');
  };
  const header = (column: Column | null, label: string, key?: string) =>
    column ? (
      <SortableTh
        key={label}
        column={column}
        label={label}
        sort={sort}
        onSort={setSort}
        {...(column === 'name' ? { className: classes.key } : {})}
      />
    ) : (
      <th key={key ?? label} scope="col">
        {label}
      </th>
    );
  return (
    <>
      <section className={table.tableWrap} aria-label={k('table')}>
        <table className={`${table.table} ${own.table} ${classes.tallRows}`}>
          <colgroup>
            <col className={own.wName} />
            <col className={own.wType} />
            <col className={own.wRank} />
            <col className={own.wOnBoard} />
            <col className={own.wPassword} />
            <col className={own.wStatus} />
            <col className={own.wActions} />
          </colgroup>
          <thead>
            <tr>
              {header('name', `${k('columns.name')} / ${k('columns.email')}`)}
              {header('type', k('columns.type'))}
              {header(null, k('columns.rank'))}
              {header('onBoard', k('columns.onBoard'))}
              {header(null, k('columns.password'))}
              {header('status', k('columns.status'))}
              <th scope="col" data-numeric>
                {k('columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((account) => {
              const actions = loginAccountActions(account, list);
              const primary = actions.find((a) => a === 'newPassword' || a === 'createLogin');
              const rest = actions.filter((a) => a !== primary);
              // Sunucunun reddedeceği işlem yerine nedeni, Settings ▸ Users'ın metniyle (U39;
              // ADR 0151 §3)
              const note = loginAccountNote(account, list);
              const password = passwordText(t, account);
              return (
                <tr key={account.userId}>
                  <td className={classes.key}>
                    <span className={classes.name}>
                      <strong title={account.fullName}>{account.fullName}</strong>
                      {account.hasLogin ? (
                        <span title={account.email}>{account.email}</span>
                      ) : (
                        <span>{k('noLogin')}</span>
                      )}
                    </span>
                  </td>
                  <td>{k(`types.${account.type}`)}</td>
                  <td>{rank(account)}</td>
                  <td>
                    {account.vessels.length === 0 ? (
                      <span className={own.line}>
                        <StatusBadge tone="neutral">{k('onBoard.officeOnly')}</StatusBadge>
                        <span className={own.detail}>{k('onBoard.officeOnlyDetail')}</span>
                      </span>
                    ) : (
                      <span className={own.lines}>
                        {account.vessels.map((line) => {
                          const text = onBoardText(
                            t,
                            line,
                            vesselById.get(line.vesselId),
                            now ? { now } : {},
                          );
                          return (
                            <span key={line.vesselId} className={own.line}>
                              <StatusBadge tone={text.tone} withIcon>
                                {text.text}
                              </StatusBadge>
                              {text.detail ? (
                                <span className={own.detail}>{text.detail}</span>
                              ) : null}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={own.password} data-tone={password.tone}>
                      {password.text}
                    </span>
                  </td>
                  <td>
                    {account.hasLogin ? (
                      <span className={classes.chips}>
                        <StatusBadge tone={account.active ? 'success' : 'danger'} variant="dot">
                          {account.active ? k('active') : k('disabled')}
                        </StatusBadge>
                        <StatusBadge tone={account.totpEnrolled ? 'info' : 'neutral'}>
                          {account.totpEnrolled ? k('totpOn') : k('totpOff')}
                        </StatusBadge>
                      </span>
                    ) : (
                      <span className={classes.chips}>
                        <StatusBadge tone="neutral">{k('noLogin')}</StatusBadge>
                        {/* Hesapsız pasif kişi (eski veri): Create login onu etkinleştirmez
                            (ADR 0150 §5); pasiflik hesap açılmadan önce de görünür */}
                        {account.active ? null : (
                          <StatusBadge tone="danger" variant="dot">
                            {k('disabled')}
                          </StatusBadge>
                        )}
                      </span>
                    )}
                  </td>
                  <td data-numeric>
                    <span className={own.actions}>
                      {primary ? (
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={
                            primary === 'newPassword' ? (
                              <IconKey size={14} aria-hidden />
                            ) : (
                              <IconUserPlus size={14} aria-hidden />
                            )
                          }
                          aria-label={
                            primary === 'newPassword'
                              ? k('newPasswordFor', { name: account.fullName })
                              : k('createLoginFor', { name: account.fullName })
                          }
                          onClick={() => onAction(account, primary)}
                        >
                          {k(`action.${primary}`)}
                        </Button>
                      ) : null}
                      {rest.length > 0 || note ? (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={k('actionsOf', { name: account.fullName })}
                            >
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            {rest.map((action) => (
                              <Menu.Item
                                key={action}
                                {...(action === 'disable' ? { color: 'red' } : {})}
                                onClick={() => onAction(account, action)}
                              >
                                {k(`action.${action}`)}
                              </Menu.Item>
                            ))}
                            {note ? (
                              <Menu.Label className={classes.menuNote}>
                                {t(`setup.users.menuNote.${note}`)}
                              </Menu.Label>
                            ) : null}
                          </Menu.Dropdown>
                        </Menu>
                      ) : null}
                      {!primary && rest.length === 0 && !note ? '—' : null}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <TablePager
        page={page_}
        pages={pages}
        summary={k('showing', {
          from: (page_ - 1) * PAGE_SIZE + 1,
          to: Math.min(page_ * PAGE_SIZE, sorted.length),
          total: sorted.length,
        })}
        label={k('pagination')}
        onPage={setPage}
      />
    </>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; list: LoginAccountList };
type Notice = { tone: 'success' | 'danger'; text: string };
type Confirming = {
  account: LoginAccountItem;
  action: 'newPassword' | 'resetTotp' | 'disable' | 'enable';
};

/**
 * Crew ▸ Login Accounts (`/crew/login-accounts`; sahip 2026-11-01, ADR 0147): giriş hesapları ve
 * gemide bugün kimin girebileceği tek sayfada. Okuma tek uç; işlemler Settings ▸ Users ve
 * personel kartının pencereleri ve uçlarıdır.
 */
export function LoginAccountsScreen({ source }: { source: LoginAccountsSource }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`loginAccounts.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [withoutLogin, setWithoutLogin] = useState(false);
  const [filter, setFilter] = useState<LoginAccountFilter>({
    query: '',
    vesselId: null,
    type: 'all',
    status: 'all',
  });
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [temporary, setTemporary] = useState<TemporaryLogin | null>(null);
  const [changing, setChanging] = useState<LoginAccountItem | null>(null);
  const [creating, setCreating] = useState<LoginAccountItem | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', list: await source.list(withoutLogin) });
    } catch (error) {
      setState({ kind: 'error', code: setupErrorCode(error) });
    }
  }, [source, withoutLogin]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state.list : null;
  const accounts = useMemo(
    () => filterLoginAccounts(ready?.accounts ?? [], filter),
    [ready, filter],
  );
  const errorText = (error: unknown) =>
    setupErrorText(t, setupErrorCode(error), setupErrorDetail(error));
  const set = <K extends keyof LoginAccountFilter>(key: K, value: LoginAccountFilter[K]) =>
    setFilter((current) => ({ ...current, [key]: value }));

  const confirmCopy: Record<Confirming['action'], { title: string; body: string; label: string }> =
    {
      newPassword: {
        title: k('action.newPassword'),
        body: 'loginAccounts.newPasswordBody',
        label: k('action.newPassword'),
      },
      resetTotp: {
        title: t('setup.users.confirm.resetTotpTitle'),
        body: 'setup.users.confirm.resetTotpBody',
        label: t('setup.users.confirm.resetTotp'),
      },
      disable: {
        title: t('setup.users.confirm.disableTitle'),
        body: 'setup.users.confirm.disableBody',
        label: t('setup.users.confirm.disable'),
      },
      enable: {
        title: t('setup.users.confirm.enableTitle'),
        body: 'setup.users.confirm.enableBody',
        label: t('setup.users.confirm.enable'),
      },
    };

  const act = async (target: Confirming, password: string): Promise<string | null> => {
    const { account, action } = target;
    try {
      if (action === 'newPassword') {
        const result = await source.resetPassword(account.userId, account.rowVersion, password);
        // Aynı pencerede bir kez gösterilir; pencere kapanınca bellekten düşer. Sıfırlama devre
        // dışı kişiyi etkinleştirmez (ADR 0150 §5): sonuç bunu söyler
        setTemporary({
          name: result.user.fullName,
          email: result.user.email,
          password: result.temporaryPassword,
          active: result.user.active ?? account.active,
        });
        void load();
        return null;
      }
      if (action === 'resetTotp') {
        await source.resetTotp(account.userId, password);
      } else {
        await source.setActive(account.userId, account.rowVersion, action === 'enable', password);
      }
      // Bildirim seçimi saf işlevde (EN/TR testli; Ek E9)
      setNotice({ tone: 'success', text: loginAccountNotice(t, action, account.fullName) });
      setConfirming(null);
      void load();
      return null;
    } catch (error) {
      return errorText(error);
    }
  };

  const total = ready?.accounts.length ?? 0;
  return (
    <div className={classes.screen}>
      <PageHeader title={k('title')} description={k('description')} />
      <p className={classes.notice} data-tone="info" role="note">
        {k('info')}
      </p>
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <Panel flush>
        <div className={classes.searchRow}>
          <div className={classes.filters}>
            <TextInput
              className={classes.search}
              leftSection={<IconSearch size={16} aria-hidden />}
              placeholder={k('search')}
              aria-label={k('search')}
              value={filter.query}
              onChange={(event) => set('query', event.currentTarget.value)}
            />
            <Select
              label={k('vessel')}
              w={200}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={filter.vesselId ?? ''}
              allowDeselect={false}
              onChange={(value) => set('vesselId', value ? value : null)}
            />
            <Select
              label={k('type')}
              w={170}
              data={[
                { value: 'all', label: k('typeAll') },
                { value: 'office', label: k('types.office') },
                { value: 'vessel', label: k('types.vessel') },
              ]}
              value={filter.type}
              allowDeselect={false}
              onChange={(value) => set('type', (value ?? 'all') as LoginAccountFilter['type'])}
            />
            <Select
              label={k('status')}
              w={150}
              data={[
                { value: 'all', label: k('statusAll') },
                { value: 'active', label: k('statusActive') },
                { value: 'disabled', label: k('statusDisabled') },
                ...(withoutLogin ? [{ value: 'noLogin', label: k('statusNoLogin') }] : []),
              ]}
              value={filter.status}
              allowDeselect={false}
              onChange={(value) => set('status', (value ?? 'all') as LoginAccountFilter['status'])}
            />
            <Checkbox
              className={own.toggle}
              label={k('withoutLogin')}
              checked={withoutLogin}
              onChange={(event) => {
                const checked = event.currentTarget.checked;
                setWithoutLogin(checked);
                if (!checked && filter.status === 'noLogin') set('status', 'all');
              }}
            />
          </div>
        </div>
        {state.kind === 'error' ? (
          state.code === 'FORBIDDEN' ? (
            <EmptyState
              tone="danger"
              title={k('forbiddenTitle')}
              description={k('forbiddenText')}
            />
          ) : (
            <LoadError title={k('loadError')} code={state.code} onRetry={() => void load()} />
          )
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={7} label={k('table')} />
        ) : total === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyText')} />
        ) : accounts.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => setFilter({ query: '', vesselId: null, type: 'all', status: 'all' })}
          />
        ) : ready ? (
          <LoginAccountsTable
            list={ready}
            accounts={accounts}
            onAction={(account, action) => {
              setNotice(null);
              if (action === 'changeEmail') setChanging(account);
              else if (action === 'createLogin') setCreating(account);
              else setConfirming({ account, action });
            }}
          />
        ) : null}
      </Panel>
      {confirming ? (
        <PasswordConfirm
          title={confirmCopy[confirming.action].title}
          body={t(confirmCopy[confirming.action].body, { name: confirming.account.fullName })}
          confirmLabel={confirmCopy[confirming.action].label}
          danger={confirming.action === 'disable'}
          result={confirming.action === 'newPassword' ? temporary : null}
          resultNote={k('newPasswordNote')}
          onClose={() => {
            setConfirming(null);
            setTemporary(null);
          }}
          onConfirm={(password) => act(confirming, password)}
        />
      ) : null}
      {changing ? (
        <ChangeEmailDialog
          user={changing}
          onClose={() => setChanging(null)}
          onConfirm={async (email, password) => {
            try {
              await source.changeEmail(changing.userId, changing.rowVersion, email, password);
              setChanging(null);
              setNotice({
                tone: 'success',
                text: loginAccountNotice(t, 'changeEmail', changing.fullName),
              });
              void load();
              return null;
            } catch (error) {
              return errorText(error);
            }
          }}
        />
      ) : null}
      {creating ? (
        <CreateLoginDialog
          view={{ userId: creating.userId, fullName: creating.fullName }}
          editor={source}
          onClose={() => setCreating(null)}
          onSaved={() => {
            setNotice({
              tone: 'success',
              text: loginAccountNotice(t, 'createLogin', creating.fullName),
            });
            setCreating(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
